import { ReactNode, useEffect, useRef } from 'react'

import * as Dialog from '@radix-ui/react-dialog'
import {
  Cross2Icon as CrossIcon,
  HamburgerMenuIcon,
} from '@radix-ui/react-icons'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { websitePaths } from 'domains/website/website.paths'
import useToggle from 'hooks/use-toggle'

import Box from 'components/atomic/atoms/box'
import { IconStyled } from 'components/atomic/atoms/icon'
import Logo from 'components/atomic/atoms/logo'
import Text from 'components/atomic/atoms/text'
import NavbarLinks from 'components/pages/@layouts/layout-website/navbar-links'
import AnnouncementBanner from 'components/pages/@shared/announcement-banner'

import SessionLinks from './session-links'

const NavbarLogo = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [open, toggle] = useToggle()
  const options = [
    {
      text: 'Download icon as .svg',
      href: '/icon.svg',
    },
    {
      text: 'Download full logo as .svg',
      href: '/logo.svg',
    },
  ]

  useEffect(() => {
    const current = ref?.current
    if (current) {
      const contextmenu = e => {
        e.preventDefault()
        toggle()
      }

      const click = () => {
        if (open) {
          toggle()
        }
      }

      current.addEventListener('contextmenu', contextmenu)

      window.document.addEventListener('click', click)

      return () => {
        current.removeEventListener('contextmenu', contextmenu)
        window.document.removeEventListener('click', click)
      }
    }
  }, [open, ref, toggle])

  return (
    <Box relative>
      <Box
        ref={ref}
        title="Right Click to download media"
        aria-label="Right Click to download media"
      >
        <Text
          as={Link}
          href={websitePaths.root}
          aria-label="Open Home page"
          color="text1"
        >
          <Logo.WithText size="md" />
        </Text>
      </Box>
      {open && (
        <Box
          absolute
          css={{
            top: '110%',
            minWidth: 250,
            boxShadow: '$lg',
            borderRadius: '$md',
            border: '1px solid $bg3',
            bgc: '$base',
            '& > *': {
              outline: 'none',
            },
          }}
        >
          {options.map(({ text, href }) => (
            <Box
              as="a"
              key={href}
              href={href}
              download
              flex
              items="center"
              css={{
                gap: '$2',
                px: '$3',
                py: '$2',
                '&:hover': { bgc: '$bg2' },
              }}
            >
              <Text as="p" size="sm" color="text4">
                ⇩
              </Text>
              <Text as="p" size="sm" color="text2">
                {text}
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

const LayoutWebsiteNavbar = ({
  customMobileLinks,
}: {
  customMobileLinks: ReactNode
}) => {
  const { events } = useRouter()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const zIndex = 5

  // Close the menu on route change (after a link click)
  useEffect(() => {
    const closeMenuOnRouteChange = () => {
      closeButtonRef?.current?.click()
    }
    events.on('routeChangeStart', closeMenuOnRouteChange)

    return () => {
      events.off('routeChangeStart', closeMenuOnRouteChange)
    }
  }, [events])

  return (
    <Box
      as="nav"
      role="navigation"
      fixed
      css={{
        zIndex,
        width: '$full',
        background: '$bg1',
        '.dark &': {
          backdropFilter: 'blur(12px)',
          background: 'rgba(22, 23, 27, 0.01)',
        },
      }}
    >
      <AnnouncementBanner />
      <Box
        flex
        items="center"
        css={{
          container: 'xl',
          height: 56,
          py: '$4',
        }}
      >
        <Box flex items="center" css={{ flex: 1 }}>
          <NavbarLogo />
        </Box>
        <Box
          css={{
            display: 'none',
            flex: 1,
            '@sm': { display: 'block' },
          }}
        >
          <NavbarLinks />
        </Box>
        <Box
          justify="end"
          css={{
            display: 'none',
            flex: 1,
            '@sm': { display: 'flex' },
          }}
        >
          <SessionLinks />
        </Box>
        {/* Mobile Navigation */}
        <Dialog.Root>
          <Dialog.Trigger asChild>
            <Box
              as="button"
              aria-label="Open mobile navigation"
              css={{
                outline: 'none',
                '&:focus, &:focus-visible': { outline: 'none' },
                '@sm': { display: 'none' },
              }}
            >
              <IconStyled as={HamburgerMenuIcon} size="xl" />
            </Box>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay />
            <Dialog.Content>
              <Box
                fixed
                css={{
                  zIndex: zIndex + 1,
                  inset: 0,
                  minHeight: '100vh',
                  bgc: '$bg1',
                  overflowY: 'auto',
                }}
              >
                <Box
                  flex
                  items="center"
                  justify="between"
                  css={{
                    container: 'xl',
                    height: 56,
                    py: '$4',
                    bgc: '$bg1',
                    borderBottom: '1px solid $bg3',
                  }}
                >
                  <NavbarLogo />
                  <Dialog.Close ref={closeButtonRef} asChild>
                    <Box
                      as="button"
                      aria-label="Close mobile navigation"
                      css={{
                        outline: 'none',
                        '&:focus, &:focus-visible': { outline: 'none' },
                        '@sm': { display: 'none' },
                      }}
                    >
                      <IconStyled as={CrossIcon} size="xl" />
                    </Box>
                  </Dialog.Close>
                </Box>
                <Box css={{ container: 'xl', pt: '$8', pb: '$12' }}>
                  {customMobileLinks ? customMobileLinks : <NavbarLinks />}
                  <Box css={{ height: 1, bgc: '$bg2', my: '$8' }} />
                  <SessionLinks />
                </Box>
              </Box>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </Box>
    </Box>
  )
}

export default LayoutWebsiteNavbar
